import { Environment } from '@40seas/entities/common';
import { IntentType } from '@40seas/entities/intents';
import { useSetupData, useSafeAuth0 } from '@40seas/frontend/libs/hooks';
import { LocaleType, datadogRumProvider, useFeatureToggleClient } from '@40seas/frontend/libs/providers';
import { PaymentOptionWithFee } from '@40seas/models';
import { Authentication, IntentsDataProvider, ModalConfiguration, useLogin } from '@40seas/payments-shared';
import { AxiosError } from 'axios';
import { createContext } from 'react';
import { initilizeIntentConfiguration } from '../contexts/IntentConfiguration';
import { PaymentContextType } from '../contexts/PaymentContextTypes';

export const PaymentContext = createContext<PaymentContextType>({} as PaymentContextType);

export const usePaymentContext = ({
    intentType,
    dataProvider,
    isLoading,
    error = null,
    environment,
    data,
    isSeller = false,
    locale = 'en',
    isAuthenticationEnabled = true,
    isLoginWithPopupEnabled = false,
    modalConfiguration,
}: {
    intentType: IntentType;
    dataProvider: IntentsDataProvider;
    locale?: LocaleType;
    isLoading: boolean;
    error?: unknown;
    environment: Environment;
    data?: PaymentContextType;
    isSeller?: boolean;
    isAuthenticationEnabled?: boolean;
    isLoginWithPopupEnabled?: boolean;
    modalConfiguration?: ModalConfiguration['options'];
}): PaymentContextType => {
    const { urls } = useSetupData();

    const featureToggleClient = useFeatureToggleClient();

    const authentication = usePaymentAuthContext({
        isAuthenticationEnabled,
        isLoginWithPopupEnabled,
    });

    // featureToggleClient may initialize without properties (e.g., tenantId),
    // causing FT values to differ from config. This patch ensures the modal
    // loads only after properties are ready for consistent FT behavior.
    if (!!data && featureToggleClient.isReady && !featureToggleClient.featureToggleProperties.key) {
        featureToggleClient.identify({
            key: data.intentType,
            tenantId: data.tenantId,
        });
    }

    if (
        isLoading ||
        !featureToggleClient.isReady ||
        (isAuthenticationEnabled && authentication?.isLoading) ||
        !featureToggleClient.featureToggleProperties.key
    ) {
        return { isLoading: true, environment, intentType } as PaymentContextType;
    }

    if (data) {
        const adjustedData = adjustDataToModalConfiguration(data, modalConfiguration);

        return {
            ...adjustedData,
            environment,
            dataProvider,
            featureToggleClient,
            configuration: initilizeIntentConfiguration(adjustedData, adjustedData.intentType, isSeller, urls, locale),
            isAuthenticationEnabled,
            authentication,
            origin: getOrigin(),
        };
    }

    // TODO: if non relevant error, pass new BusinessLogicError()
    const errorMessage = datadogRumProvider.getErrorMessage(error);
    return {
        error: {
            message:
                errorMessage === 'Network Error'
                    ? `Error occurred. Message: ${datadogRumProvider.getErrorMessage(error)}. Url: ${(error as AxiosError).config?.url}`
                    : errorMessage,
        },
        environment,
    } as PaymentContextType;
};

const usePaymentAuthContext = ({
    isAuthenticationEnabled,
    isLoginWithPopupEnabled,
}: {
    isAuthenticationEnabled: boolean;
    isLoginWithPopupEnabled: boolean;
}): Authentication => {
    const { user, isAuthenticated, getAccessTokenSilently, logout, isLoading, loginWithPopup, loginWithRedirect } = useSafeAuth0();
    const { login, signup, loginWithGoogle, loginWithMicrosoft, resendVerification, verifyCode } = useLogin();

    if (!isAuthenticationEnabled) {
        return { isLoading: false };
    }

    return {
        isLoading: isLoading,
        getAccessTokenSilently,
        legacyLogin: isLoginWithPopupEnabled ? loginWithPopup : loginWithRedirect,
        isAuthenticated,
        email: user?.email,
        login,
        logout,
        signup,
        loginWithGoogle,
        loginWithMicrosoft,
        resendVerification,
        verifyCode,
    };
};

const adjustDataToModalConfiguration = (data: PaymentContextType, modalConfiguration: any): PaymentContextType => {
    const modalConfigurationPaymentMethods = modalConfiguration?.[data.intentType]?.paymentMethodTypes;
    const modalConfigurationPaymentTerms = modalConfiguration?.[data.intentType]?.paymentTerms;

    if ((!modalConfigurationPaymentMethods && !modalConfigurationPaymentTerms) || data.intentType !== IntentType.Checkout) {
        return data;
    }

    let filteredPaymentOptions = data.paymentOptions;

    if (modalConfigurationPaymentMethods) {
        filteredPaymentOptions = filteredPaymentOptions?.filter((paymentOption: PaymentOptionWithFee) =>
            modalConfigurationPaymentMethods?.includes(paymentOption.paymentOption.paymentMethod),
        );
    }

    if (modalConfigurationPaymentTerms) {
        filteredPaymentOptions = filteredPaymentOptions?.filter((paymentOption: PaymentOptionWithFee) =>
            modalConfigurationPaymentTerms?.includes(paymentOption.paymentOption.paymentTerm.type),
        );
    }

    const amount = data.amount ?? modalConfiguration?.checkout?.amount;
    const currency = data.currency ?? modalConfiguration?.checkout?.currency;

    return { ...data, paymentOptions: filteredPaymentOptions, amount, currency };
};

function getOrigin(): string | undefined {
    return new URL(window.location.href).searchParams.get('origin') ?? undefined;
}
